import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import AddIcon from "@mui/icons-material/Add";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";

import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  deleteExercise,
  fetchExercises,
  saveNewExercise,
} from "../../services/WodService";
import Lottie from "react-lottie";
import RocketImage from "../../assets/empty.json";
import LoadingImage from "../../assets/loading.json";
import { SystemDialog } from "../../utils/Dialog";
import { useSnackbar } from "notistack";
import { withAuth } from "../../hoc";

const emptyOptions = {
  loop: true,
  autoplay: true,
  animationData: RocketImage,
};

const loadingOptions = {
  loop: true,
  autoplay: true,
  animationData: LoadingImage,
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 15,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const Exercises = () => {
  const [exercises, setExercises] = useState<
    Array<{ name: string; id: string }>
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [newExercise, setNewExerice] = useState<string>("");

  const [currentExercise, setCurrentExercise] = useState<string | undefined>(
    undefined
  );

  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] =
    useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const { enqueueSnackbar } = useSnackbar();

  const getAllExercises = () => {
    setLoading(true);
    fetchExercises()
      .then((exerciseList) => {
        setExercises(exerciseList);
        setLoading(false);
        setPage((prev) =>
          prev >= Math.ceil(exerciseList.length / rowsPerPage)
            ? Math.max(0, Math.ceil(exerciseList.length / rowsPerPage) - 1)
            : prev
        );
      })
      .catch((e) => {
        setLoading(false);
        alert("Something went wrong! Please try again later...");
      });
  };

  useEffect(() => {
    getAllExercises();
  }, []);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onDeleteExercise = (exercise: string) => {
    setCurrentExercise(exercise);
    onExerciseDeleteClicked();
  };

  const onExerciseDeleteClicked = () => {
    setShowDeleteConfirmDialog(true);
  };

  const handleDeleteConfirm = () => {
    deleteExercise(currentExercise || "")
      .then(() => {
        setShowDeleteConfirmDialog(false);
        enqueueSnackbar("Exercise deleted successfully!");
        setCurrentExercise(undefined);
        getAllExercises();
      })
      .catch(() => {
        setShowDeleteConfirmDialog(false);
      });
  };

  const onNewExerciseChange = (event: any) => {
    setNewExerice(event.target.value);
  };

  const handleNewExerciseAdd = () => {
    if (
      exercises.findIndex(
        (ex) => ex.id === newExercise.toLowerCase().trim()
      ) === -1
    ) {
      saveNewExercise(newExercise)
        .then(() => {
          enqueueSnackbar(`${newExercise} saved successfully!`);
          setNewExerice("");
          getAllExercises();
        })
        .catch(() => alert("Something went wrong, please try again later."));
    } else {
      alert("This exercise already exist!");
    }
  };

  const EmptyData = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <Lottie options={emptyOptions} height={120} width={120} />
        <Typography
          variant="body2"
          sx={{ textAlign: "center", mt: 2, color: "text.secondary" }}
        >
          No exercises found. Add your first exercise to get started!
        </Typography>
      </Box>
    );
  };

  const Loading = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <Lottie options={loadingOptions} height={200} width={200} />
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "text.secondary" }}
        >
          Loading your exercises...
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 2, md: 3 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <FitnessCenterIcon sx={{ color: "secondary.main", fontSize: 28 }} />
          <Typography variant="h5" fontWeight={600}>
            Exercises
          </Typography>
          {!loading && exercises.length > 0 && (
            <Chip
              label={exercises.length}
              size="small"
              color="secondary"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>

      {/* Add Exercise Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          mb: 3,
          flexShrink: 0,
        }}
      >
        <CardContent
          sx={{
            p: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Add Exercise
            </Typography>
          </Box>
          <TextField
            margin="none"
            id="exercise-add"
            label="Exercise Name"
            placeholder="e.g. Burpees, Mountain Climbers..."
            value={newExercise}
            type="text"
            onChange={onNewExerciseChange}
            fullWidth
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FitnessCenterIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <Button
            color="secondary"
            variant="contained"
            onClick={handleNewExerciseAdd}
            disabled={!newExercise.trim()}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              py: 1,
              px: 3,
              borderRadius: 1.5,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            Add Exercise
          </Button>
        </CardContent>
      </Card>

      {/* Exercise List */}
      {exercises.length === 0 || loading ? (
        loading ? (
          <Loading />
        ) : (
          <EmptyData />
        )
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 0,
              overflow: "auto",
            }}
          >
            <Table stickyHeader aria-label="exercises table">
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ width: 60 }}>#</StyledTableCell>
                  <StyledTableCell>Exercise Name</StyledTableCell>
                  <StyledTableCell align="right" sx={{ width: 80 }}>
                    Actions
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exercises
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((exercise, index) => (
                    <StyledTableRow key={exercise.id}>
                      <StyledTableCell>
                        <Typography variant="body2" color="text.secondary">
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        <Typography variant="body1">{exercise.name}</Typography>
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <Tooltip title="Delete exercise" arrow>
                          <IconButton
                            onClick={() => onDeleteExercise(exercise.name)}
                            size="small"
                            sx={{
                              color: "text.secondary",
                              "&:hover": {
                                color: "error.main",
                                backgroundColor: "error.dark",
                                bgcolor: "rgba(244,67,54,0.08)",
                              },
                            }}
                            aria-label="delete exercise"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={exercises.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              flexShrink: 0,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          />
        </Box>
      )}

      <SystemDialog
        component={
          <Typography variant="overline">
            {`Are you sure you want to delete this exercise ?`}
          </Typography>
        }
        open={showDeleteConfirmDialog}
        handleClose={() => setShowDeleteConfirmDialog(false)}
        handleConfirm={handleDeleteConfirm}
        heading="Delete Exercise"
      />
    </Box>
  );
};

export default withAuth(Exercises);
